import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm, useController } from "react-hook-form";
import { IMaskInput } from "react-imask";
import IMask from "imask";
import Navbar from "@/components/feature/Navbar";

function GeometricShapes() {
  return (
    <div className="geometric-shapes">
      <div className="geo-shape triangle" />
      <div className="geo-shape diamond" />
      <div className="geo-shape circle" />
    </div>
  );
}

type FormData = {
  name: string;
  email: string;
  date_of_transaction: string;
  amount: string;
  currency: string;
  card_first6: string;
  card_last4: string;
  description: string;
};

const currentYear = new Date().getFullYear();

export default function DisputeResolution() {
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ mode: "onTouched" });

  const { field: dateField } = useController({
    name: "date_of_transaction",
    control,
    rules: {
      required: "Date is required",
      validate: (value) => {
        if (!value || value.replace(/\D/g, "").length < 8) return "Enter a complete date";
        const [dd, mm, yyyy] = value.split("/").map(Number);
        const entered = new Date(yyyy, mm - 1, dd);
        if (isNaN(entered.getTime())) return "Invalid date";
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (entered > today) return "Date cannot be in the future";
        return true;
      },
    },
  });

  const { field: first6Field } = useController({
    name: "card_first6",
    control,
    rules: {
      required: "Required",
      validate: (v) => v.replace(/\D/g, "").length === 6 || "Must be 6 digits",
    },
  });

  const { field: last4Field } = useController({
    name: "card_last4",
    control,
    rules: {
      required: "Required",
      validate: (v) => v.replace(/\D/g, "").length === 4 || "Must be 4 digits",
    },
  });

  const { field: amountField } = useController({
    name: "amount",
    control,
    rules: {
      required: "Amount is required",
      validate: (v) => {
        const num = Number(v.replace(",", "."));
        if (isNaN(num) || v === "") return "Enter a valid number";
        if (num <= 0) return "Amount must be greater than 0";
        return true;
      },
    },
  });

  const descValue = watch("description", "");

  const onSubmit = async (data: FormData) => {
    setServerError(null);

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: "Transaction Issue",
      to_email: "support@trevionics.com",
      subject: "Dispute Resolution Request",
    };

    try {
      const res = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
      reset();
    } catch {
      setServerError("Failed to send request. Please try again or contact us directly.");
    }
  };

  const inputCls = (hasError: boolean) =>
    `w-full px-5 py-3.5 rounded-lg border text-sm text-[#1A1C1E] placeholder-[#9CA3AF] outline-none transition-colors bg-white ${
      hasError
        ? "border-[#B8422E] focus:border-[#B8422E]"
        : "border-[#D1D5DB] focus:border-[#1A1C1E]"
    }`;

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? <p className="mt-1.5 text-xs text-[#B8422E]">{msg}</p> : null;

  return (
    <div className="relative min-h-screen">
      <GeometricShapes />

      <div className="relative z-10">
        <Navbar />


        <section className="bg-[#0d0d0d] pt-20">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-12 md:py-20">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] font-medium mb-4">/ Disputes</p>
            <h1 className="text-3xl md:text-4xl lg:text-[48px] font-extrabold text-white tracking-[-0.02em] leading-tight">
              File a<br /><span className="text-[#B8422E]">Dispute Resolution</span>
            </h1>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-[#1A1C1E] flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-[#1A1C1E] mb-3">Dispute Submitted</h3>
                <p className="text-[#6C7278] mb-8 max-w-md mx-auto leading-relaxed">
                  Thank you for submitting your dispute resolution request. Our team will review your case and get back to you within 3–5 business days.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-[#1A1C1E] text-white font-medium text-sm px-8 py-3 rounded-xl hover:bg-black transition-colors"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-10">

                {/* Row 1: Name + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Name</label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      className={inputCls(!!errors.name)}
                      {...register("name", {
                        required: "Name is required",
                        minLength: { value: 2, message: "At least 2 characters" },
                      })}
                    />
                    <ErrorMsg msg={errors.name?.message} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Email</label>
                    <input
                      type="email"
                      placeholder="your.email@example.com"
                      className={inputCls(!!errors.email)}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: "Enter a valid email address",
                        },
                      })}
                    />
                    <ErrorMsg msg={errors.email?.message} />
                  </div>
                </div>

                {/* Row 2: Date of Transaction + Amount */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Date of Transaction</label>
                    <IMaskInput
                      mask="DD/MM/YYYY"
                      blocks={{
                        DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
                        MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
                        YYYY: { mask: IMask.MaskedRange, from: 2000, to: currentYear },
                      }}
                      placeholder="DD/MM/YYYY"
                      className={inputCls(!!errors.date_of_transaction)}
                      value={dateField.value ?? ""}
                      onAccept={(value: string) => dateField.onChange(value)}
                      onBlur={dateField.onBlur}
                      inputRef={dateField.ref}
                    />
                    <ErrorMsg msg={errors.date_of_transaction?.message} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Amount</label>
                    <IMaskInput
                      mask={Number}
                      scale={2}
                      min={0}
                      max={100}
                      normalizeZeros
                      radix="."
                      mapToRadix={[","]}
                      placeholder="0.00"
                      className={inputCls(!!errors.amount)}
                      value={amountField.value ?? ""}
                      onAccept={(value: string) => amountField.onChange(value)}
                      onBlur={amountField.onBlur}
                      inputRef={amountField.ref}
                    />
                    <ErrorMsg msg={errors.amount?.message} />
                  </div>
                </div>
 
                {/* Row 3: Currency + Card digits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Currency</label>
                    <select
                      className={`${inputCls(!!errors.currency)} appearance-none cursor-pointer`}
                      style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27%236C7278%27 d=%27M2 4l4 4 4-4z%27/%3E%3C/svg%3E')", backgroundRepeat: "no-repeat", backgroundPosition: "right 16px center" }}
                      {...register("currency", { required: "Please select a currency" })}
                    >
                      <option value="">Select currency</option>
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                    <ErrorMsg msg={errors.currency?.message} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">First 6 digits</label>
                      <IMaskInput
                        mask="000000"
                        placeholder="123456"
                        className={inputCls(!!errors.card_first6)}
                        value={first6Field.value ?? ""}
                        onAccept={(value: string) => first6Field.onChange(value)}
                        onBlur={first6Field.onBlur}
                        inputRef={first6Field.ref}
                      />
                      <ErrorMsg msg={errors.card_first6?.message} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Last 4 digits</label>
                      <IMaskInput
                        mask="0000"
                        placeholder="1234"
                        className={inputCls(!!errors.card_last4)}
                        value={last4Field.value ?? ""}
                        onAccept={(value: string) => last4Field.onChange(value)}
                        onBlur={last4Field.onBlur}
                        inputRef={last4Field.ref}
                      />
                      <ErrorMsg msg={errors.card_last4?.message} />
                    </div>
                  </div>
                </div>

                {/* Row 4: Issue Description */}
                <div>
                  <label className="block text-sm font-semibold text-[#1A1C1E] mb-3">Issue Description</label>
                  <textarea
                    rows={8}
                    maxLength={500}
                    placeholder="Please provide detailed information about your dispute. Include what happened, when it occurred, and what resolution you are seeking."
                    className={`${inputCls(!!errors.description)} resize-none`}
                    {...register("description", {
                      required: "Description is required",
                      minLength: { value: 20, message: "Minimum 20 characters required" },
                      maxLength: { value: 500, message: "Maximum 500 characters allowed" },
                    })}
                  />
                  <div className="flex items-center justify-between mt-3">
                    {errors.description ? (
                      <p className="text-xs text-[#B8422E]">{errors.description.message}</p>
                    ) : (
                      <span className="text-xs text-[#9CA3AF]">Minimum 20 characters required</span>
                    )}
                    <span className={`text-sm font-medium ${(descValue?.length ?? 0) > 480 ? "text-[#B8422E]" : "text-[#1A1C1E]"}`}>
                      {descValue?.length ?? 0} / 500
                    </span>
                  </div>
                </div>

                {serverError && (
                  <div className="flex items-center gap-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-5 py-4">
                    <i className="ri-error-warning-line text-[#B8422E] text-lg flex-shrink-0" />
                    <p className="text-sm text-[#B8422E]">{serverError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1A1C1E] text-white font-semibold text-sm py-4 rounded-xl hover:bg-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        <footer className="bg-[#0d0d0d] border-t border-white/[0.06]">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto py-16 md:py-20">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 pb-10 md:pb-12 border-b border-white/[0.06]">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-base font-bold tracking-[0.16em] text-white uppercase">Trevionix Help</span>
                  <span className="w-px h-4 bg-white/20"></span>
                  <span className="text-[11px] tracking-[0.12em] text-[#6C7278] uppercase">LTD</span>
                </div>
                <p className="text-sm text-[#6C7278] max-w-[260px] leading-relaxed">
                  12 Constance Street<br />
                  London, E16 2DQ<br />
                  United Kingdom
                </p>
                <a href="mailto:support@trevionics.com" className="mt-3 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors">
                  <i className="ri-mail-line text-[#B8422E] text-xs" />
                  support@trevionics.com
                </a>
                <a href="tel:+447520658798" className="mt-1.5 flex items-center gap-2 text-sm text-[#6C7278] hover:text-white transition-colors">
                  <i className="ri-phone-line text-[#B8422E] text-xs" />
                  +44 7520 658798
                </a>
              </div>
            </div>
            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#6C7278]">&copy; 2026 Trevionix LTD. All rights reserved.</p>
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-xs text-[#6C7278] hover:text-white transition-colors"
              >
                <i className="ri-arrow-left-line" />
                Back to Help Center
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
